import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Headphones, Clapperboard, BookOpen, ChefHat, ScrollText, Tv, Film,
  RefreshCw, Play,
} from "lucide-react";
import { api, TYPE_META, PICK_ELIGIBLE_TYPES } from "@/lib/api";
import { Fleuron } from "@/components/Ornaments";

const PICK_META = {
  article: { label: "Today's read",      verb: "Read",   Slot: BookOpen },
  book:    { label: "Today's chapter",   verb: "Read",   Slot: BookOpen },
  music:   { label: "Today's listen",    verb: "Listen", Slot: Headphones },
  video:   { label: "Today's clip",      verb: "Watch",  Slot: Play },
  movie:   { label: "Today's screening", verb: "Watch",  Slot: Play },
  tvshow:  { label: "Today's episode",   verb: "Watch",  Slot: Play },
  recipe:  { label: "Today's recipe",    verb: "Cook",   Slot: ChefHat },
};

const LS_KEY = "lc:todays_pick";

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function pickOne(items, type, exclude = new Set()) {
  // Today's Pick is strictly for things you haven't consumed yet — once an item
  // is marked played/watched, it is invisible to this widget. If every item of
  // a type is already played we surface nothing for that type (better to show
  // an empty slot than resurface something the user just finished).
  const pool = items.filter(i => i.type === type && !i.played && !exclude.has(i.id));
  if (pool.length === 0) return null;
  return pool[Math.floor(Math.random() * pool.length)];
}

function vibrate(ms = 14) {
  try { if (navigator.vibrate) navigator.vibrate(ms); } catch { /* no-op */ }
}

export default function TodaysPick({ items }) {
  const [picks, setPicks] = useState({});
  const [flipNonce, setFlipNonce] = useState(0);
  const mounted = useRef(false);

  const computeFresh = useCallback(() => {
    const next = {};
    for (const type of PICK_ELIGIBLE_TYPES) {
      const it = pickOne(items, type);
      if (it) next[type] = it;
    }
    return next;
  }, [items]);

  const persist = (p) => {
    const ids = {};
    for (const k of Object.keys(p)) ids[k] = p[k]?.id;
    try { localStorage.setItem(LS_KEY, JSON.stringify({ day: todayKey(), ids })); } catch { /* ignore */ }
  };

  useEffect(() => {
    if (mounted.current) return;
    mounted.current = true;
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const cached = JSON.parse(raw);
        if (cached.day === todayKey()) {
          const out = {};
          for (const type of PICK_ELIGIBLE_TYPES) {
            const id = cached.ids?.[type];
            const found = id && items.find(i => i.id === id);
            // Drop any cached pick that was marked played since last visit —
            // it will be replaced by a fresh unplayed pick below.
            if (found && !found.played) out[type] = found;
          }
          // If every eligible type is missing (all played today), fall through
          // to computeFresh so we can surface newly-added items.
          if (Object.keys(out).length > 0) {
            // Fill in any missing types with a fresh unplayed pick.
            const exclude = new Set(Object.values(out).map(p => p?.id).filter(Boolean));
            for (const type of PICK_ELIGIBLE_TYPES) {
              if (out[type]) continue;
              const it = pickOne(items, type, exclude);
              if (it) { out[type] = it; exclude.add(it.id); }
            }
            setPicks(out);
            persist(out);
            return;
          }
        }
      }
    } catch { /* ignore */ }
    const fresh = computeFresh();
    setPicks(fresh);
    persist(fresh);
  }, [computeFresh, items]);

  function reshuffle() {
    vibrate(12);
    const exclude = new Set(Object.values(picks).map(p => p?.id).filter(Boolean));
    const next = {};
    for (const type of PICK_ELIGIBLE_TYPES) {
      const it = pickOne(items, type, exclude) || pickOne(items, type);
      if (it) next[type] = it;
    }
    setPicks(next);
    persist(next);
    setFlipNonce(n => n + 1);
  }

  const visible = useMemo(() => PICK_ELIGIBLE_TYPES.filter(t => picks[t]), [picks]);
  if (visible.length === 0) return null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      data-testid="todays-pick"
      className="relative"
    >
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Fleuron size={18} color="var(--bronze)" />
          <h2 className="font-cinzel text-[12px] tracking-[0.28em] uppercase text-[var(--bronze)]">
            Today&apos;s pick
          </h2>
        </div>
        <button
          type="button"
          onClick={reshuffle}
          data-testid="reshuffle-btn"
          className="flex items-center gap-2 text-[var(--ink-dusty)] hover:text-[var(--bronze)] transition-colors"
          title="Pick again"
        >
          <RefreshCw size={13} strokeWidth={1.4} />
          <span className="font-cinzel text-[10px] tracking-[0.22em] uppercase">Reshuffle</span>
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" style={{ perspective: "1200px" }}>
        {visible.map((type, idx) => {
          const it = picks[type];
          const m = PICK_META[type];
          const meta = TYPE_META[type];
          return (
            <FlipCard
              key={`${type}-${it.id}-${flipNonce}`}
              delay={idx * 0.08}
              item={it} type={type} label={m.label} verb={m.verb} Slot={m.Slot} meta={meta}
            />
          );
        })}
      </div>
    </motion.section>
  );
}

function FlipCard({ item, type, label, verb, Slot, meta, delay }) {
  const openSource = (e) => {
    if (!item.source_url) return;
    e.preventDefault(); e.stopPropagation();
    window.open(item.source_url, "_blank", "noopener,noreferrer");
    if (!item.played) api.patch(`/items/${item.id}`, { played: true }).catch(() => {});
  };
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={item.id}
        initial={{ rotateX: -90, opacity: 0 }}
        animate={{ rotateX: 0, opacity: 1 }}
        exit={{ rotateX: 90, opacity: 0 }}
        transition={{ duration: 0.55, delay, ease: [0.22, 0.61, 0.36, 1] }}
        style={{ transformStyle: "preserve-3d", transformOrigin: "center center" }}
      >
        <Link
          to={`/item/${item.id}`}
          data-testid={`pick-${type}`}
          className="placed-card relative overflow-hidden flex group flip-card-face"
          style={{ borderColor: meta.border, background: `linear-gradient(180deg, ${meta.bg}40, var(--card-bg))` }}
        >
          {/* hinge line — like an analogue flip clock */}
          <span className="absolute left-0 right-0 top-1/2 h-px bg-[rgba(42,30,20,0.06)] pointer-events-none" />
          <div className="w-24 shrink-0" style={{ background: meta.bg }}>
            {item.image_url ? (
              <img src={item.image_url} alt="" className="w-full h-full object-cover" loading="lazy"/>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Slot size={26} strokeWidth={1} style={{ color: meta.accent, opacity: 0.55 }}/>
              </div>
            )}
          </div>
          <div className="flex-1 p-4 min-w-0">
            <p className="font-cinzel text-[9px] tracking-[0.26em] uppercase" style={{ color: meta.accent }}>
              {label}
            </p>
            <h3 className="mt-2 font-serif-lit text-base leading-snug text-[var(--ink)] line-clamp-2">
              {item.title || "Untitled"}
            </h3>
            {item.creator && (
              <p className="mt-0.5 text-xs font-garamond italic text-[var(--ink-dusty)] line-clamp-1">{item.creator}</p>
            )}
            {item.source_url && (
              <button
                type="button"
                onClick={openSource}
                data-testid={`pick-action-${type}`}
                className="mt-2 inline-flex items-center gap-1.5 font-cinzel text-[10px] tracking-[0.22em] uppercase hover:underline"
                style={{ color: meta.accent }}
              >
                <Slot size={11} strokeWidth={1.6}/> {verb} now
              </button>
            )}
          </div>
        </Link>
      </motion.div>
    </AnimatePresence>
  );
}
